import React, { useState, useRef, useEffect, useMemo } from "react";
import InnoIcons from "@InnoIcons/";
import SC from "./style";

const MultiSelectDropdown = ({
  label,
  options = [],
  headerOptionsValueName = "Occupant",
  selectedHeaderId,
  onHeaderChange,
  selectedIds = [], // controlled selection
  onSelectionChange, // renamed for clarity
  minWidth = "270px",
  buttonWidth = "200px",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef();

  const headerOptions = [
    { id: "equals", label: `${headerOptionsValueName} = (equals)` },
    { id: "not-equals", label: `${headerOptionsValueName} != (not equals)` },
  ];

  const selectedHeader = headerOptions.find((h) => h.id === selectedHeaderId);

  // ✅ Memoized filter for performance
  const filteredOptions = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerSearch)
    );
  }, [options, searchTerm]);

  const handleOptionClick = (optionId) => {
    const idStr = String(optionId);
    const updatedIds = selectedIds.includes(idStr)
      ? selectedIds.filter((id) => String(id) !== idStr)
      : [...selectedIds, idStr];

    onSelectionChange(updatedIds);
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleHeaderOptionClick = (headerId) => {
    onHeaderChange(headerId);
    setIsHeaderExpanded(false);
  };

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsHeaderExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setIsHeaderExpanded(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <SC.DropdownContainer ref={dropdownRef}>
      <SC.DropdownButton
        minWidth={buttonWidth}
        isOpen={isOpen}
        isActive={selectedIds.length > 0}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedIds.length > 0
          ? (() => {
              const operator = selectedHeaderId === "not-equals" ? "!=" : "=";
              const selectedNames = selectedIds.map((id) => {
                const match = options.find((o) => String(o.id) === String(id));
                return match ? match.label : id;
              });

              const maxVisible = 2;
              const visibleNames = selectedNames
                .slice(0, maxVisible)
                .join(", ");
              const remainingCount = selectedNames.length - maxVisible;

              return (
                <>
                  {`${label}: ${operator} ${visibleNames}`}
                  {remainingCount > 0 && (
                    <SC.RemainingCount> +{remainingCount}</SC.RemainingCount>
                  )}
                </>
              );
            })()
          : label}
        <SC.ChevronIcon isOpen={isOpen} isActive={selectedIds.length > 0} />
      </SC.DropdownButton>

      <SC.DropdownMenu isOpen={isOpen} minWidth={minWidth}>
        {/* Header Options */}
        {headerOptions.length > 0 && (
          <>
            <SC.DropdownHeader
              isClickable
              onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
            >
              <SC.HeaderText>
                {selectedHeader?.label || "Select Header"}
              </SC.HeaderText>
              <SC.HeaderChevron isExpanded={isHeaderExpanded} />
            </SC.DropdownHeader>

            <SC.HeaderOptionsContainer isVisible={isHeaderExpanded}>
              {headerOptions.map((header) => (
                <SC.HeaderOption
                  key={header.id}
                  isSelected={header.id === selectedHeaderId}
                  onClick={() => handleHeaderOptionClick(header.id)}
                >
                  {header.label}
                </SC.HeaderOption>
              ))}
            </SC.HeaderOptionsContainer>
          </>
        )}

        {/* Search Box */}
        <SC.SearchContainer>
          <SC.SearchIcon />
          <SC.SearchInput
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SC.SearchContainer>

        {/* Options List */}
        <SC.OptionsList>
          {filteredOptions.map((option) => (
            <SC.OptionItem
              key={option.id}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <SC.Checkbox
                type="checkbox"
                checked={selectedIds.map(String).includes(String(option.id))}
                onChange={() => handleOptionClick(option.id)} // ✅ fixed
                style={{ marginRight: "8px" }}
              />
              {option.label}
            </SC.OptionItem>
          ))}
        </SC.OptionsList>

        {/* Footer */}
        <SC.DropdownFooter>
          <SC.ClearAllButton onClick={handleClearAll}>
            Clear All
          </SC.ClearAllButton>
        </SC.DropdownFooter>
      </SC.DropdownMenu>
    </SC.DropdownContainer>
  );
};

export default MultiSelectDropdown;
